import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { getFormatedCurrency } from '@/pages/JobsList/JobsList.utils';
import { PersonType, Job } from '@/types/api';
import { getFormattedStatus } from '@/utils/format';
import * as Styles from './CandidateJobSection.styles';
import Menu from '../Menu';

type Props = {
  job: Job;
  candidate?: PersonType;
  onDisassociateFromJobClick?: () => void;
  disabled?: boolean;
};

const CandidateJobSection = ({
  candidate,
  job,
  onDisassociateFromJobClick,
  disabled = false,
}: Props): JSX.Element => {
  const salaryMin = getFormatedCurrency(job.salaryRange.min);
  const salaryMax = getFormatedCurrency(job.salaryRange.max);
  const navigate = useNavigate();

  const jobLink = `/hiring/jobs/${job.id}`;

  const onGoToJobPage = (): void => {
    navigate(jobLink);
  };

  return (
    <Styles.Section id={job.id} $disabled={disabled}>
      <Styles.Header>
        <Styles.TitleContainer>
          <Styles.Title>
            {`${job.title}${job.account ? ` | ${job.account.name}` : ''}`}
          </Styles.Title>
          <Styles.JobLink href={jobLink}>
            <OpenInNewIcon className="!tw-w-4 !tw-h-4 tw-mr-0.5 fill-primary-60" />
            See Full Job Description
          </Styles.JobLink>
        </Styles.TitleContainer>
        <Menu
          icon={MoreHorizIcon}
          menuId="delete-candidate-button"
          menuItems={[
            ...(onDisassociateFromJobClick
              ? [
                  {
                    label: 'Disassociate from this job',
                    menuItemOptions: { warning: true },
                    menuItemIcon: DeleteIcon,
                    callback: (e) => {
                      e.stopPropagation();
                      onDisassociateFromJobClick?.();
                    },
                  },
                ]
              : []),
            {
              label: 'Go to Job Description Page',
              menuItemIcon: OpenInNewIcon,
              callback: (e) => {
                e.stopPropagation();
                onGoToJobPage();
              },
            },
          ]}
        />
      </Styles.Header>
      <Styles.Row>
        <Styles.Column>
          <Styles.ColumnHeader>Job’s Requirements</Styles.ColumnHeader>
          <Styles.Table>
            <tbody>
              <tr>
                <Styles.TdLabel>Job Status</Styles.TdLabel>
                <Styles.TdStatus>{job.status === 'Active' ? 'Open' : job.status}</Styles.TdStatus>
              </tr>
              <tr>
                <Styles.TdLabel>When to Start</Styles.TdLabel>
                <Styles.TdValue>{job.whenToStart}</Styles.TdValue>
              </tr>
              <tr>
                <Styles.TdLabel>Salary Range</Styles.TdLabel>
                <Styles.TdValue>{`$ ${salaryMin} to $ ${salaryMax}`}</Styles.TdValue>
              </tr>
              <tr>
                <Styles.TdLabel>Developer Type</Styles.TdLabel>
                <Styles.TdValue>{job.developerType}</Styles.TdValue>
              </tr>
              <tr>
                <Styles.TdLabel>Experience</Styles.TdLabel>
                <Styles.TdValue>{job.experience || '-'}</Styles.TdValue>
              </tr>
              <tr>
                <Styles.TdLabel>Tech Stack</Styles.TdLabel>
                <td>
                  <Styles.Tags>
                    {job.techs.map((tech) => (
                      <Styles.Tag key={tech.id}>{tech.name}</Styles.Tag>
                    ))}
                  </Styles.Tags>
                </td>
              </tr>
            </tbody>
          </Styles.Table>
        </Styles.Column>
        <Styles.Column>
          <Styles.ColumnHeader>Candidate’s Info</Styles.ColumnHeader>
          {candidate && (
            <Styles.Table>
              <tbody>
                <tr>
                  <Styles.TdLabel>Candidate Status</Styles.TdLabel>
                  <Styles.TdStatus>
                    {candidate.status ? getFormattedStatus(candidate.status) : '-'}
                  </Styles.TdStatus>
                </tr>
                <tr>
                  <Styles.TdLabel>Availability</Styles.TdLabel>
                  <Styles.TdValue>{candidate.availability || '-'}</Styles.TdValue>
                </tr>
                <tr>
                  <Styles.TdLabel>Salary Expectations</Styles.TdLabel>
                  <Styles.TdValue>{candidate.salaryExpectation || '-'}</Styles.TdValue>
                </tr>
                <tr>
                  <Styles.TdLabel>Developer Type</Styles.TdLabel>
                  <Styles.TdValue>{candidate.developerType || '-'}</Styles.TdValue>
                </tr>
                <tr>
                  <Styles.TdLabel>Experience</Styles.TdLabel>
                  <Styles.TdValue>{candidate.experience || '-'}</Styles.TdValue>
                </tr>
                <tr>
                  <Styles.TdLabel>Main Tech Stacks</Styles.TdLabel>
                  <td>
                    <Styles.Tags>
                      {(candidate.mainTechStack || []).map((tech) => (
                        <Styles.Tag key={tech.id}>{tech.tech.name}</Styles.Tag>
                      ))}
                    </Styles.Tags>
                  </td>
                </tr>
              </tbody>
            </Styles.Table>
          )}
        </Styles.Column>
      </Styles.Row>
    </Styles.Section>
  );
};

export default CandidateJobSection;
